import React, {
  useContext, useState, useEffect, lazy, Suspense
} from 'react';
import {
  Alert, Button, DrawerBody, DrawerFooter, Link, Typography
} from '@one-thd/sui-atomic-components';
import proptypes from 'prop-types';
import { useConfigService, useStore } from '@thd-nucleus/experience-context';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AddToContext } from '../../AddToContext';

// Dynamic import to prevent BoughtTogetherCarousel from loading until needed
// This avoids dataModel merge conflicts on PIP page
const BoughtTogetherCarousel = lazy(() => (
  import('../../common/bought-together-carousel/BoughtTogetherCarousel').then(module => ({
    default: module.BoughtTogetherCarousel
  }))
));

const { string, func, shape, arrayOf } = proptypes;

const AddToListSuccess = ({
  newListDetails,
  onClose,
  products = []
}) => {
  const { customerType } = useThdCustomer() || {};
  const { selectedListId, selectedListName } = useContext(AddToContext);
  const fsRecsPodEnabled = useConfigService('fs:isRecsPodEnabled');

  // Client-side only state to prevent SSR hydration mismatches
  const [isClient, setIsClient] = useState(false);
  const [itemsAdded, setItemsAdded] = useState([]);

  // Initialize client-side flag safely
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Get the first product's itemId for recommendations
  const firstProductId = products?.[0]?.itemId;

  // Check if this is B2B customer and came from PDP/PIP
  const isB2BCustomer = customerType === 'B2B';

  // SSR-safe store data access
  const isSSREnabled = !(typeof window !== 'undefined' && window.DISABLE_SSR);
  const { membershipInformation = null, storeId } = useStore({ varnish: isSSREnabled, online: false });
  const loyaltyMembership = membershipInformation?.data?.loyaltyMembership;

  const defaultVariables = {
    skipInstallServices: true,
    storeId,
    loyaltyMembershipInput: loyaltyMembership,
    isBrandPricingPolicyCompliant: true
  };

  const listName = selectedListName || newListDetails.listName;
  const listId = selectedListId || newListDetails.listId;

  const hasFbtItems = itemsAdded.length > 0;

  // Only show carousel when client-side and all conditions are met, including valid listId
  const shouldShowCarousel = isClient && isB2BCustomer && firstProductId && fsRecsPodEnabled && listId && !hasFbtItems;

  // Determine the correct count for pluralization
  const itemCount = itemsAdded.length + products.length;
  const itemText = itemCount > 1 ? 'items have' : 'item has';

  return (
    <>
      <DrawerBody>
        <Alert status="success" size="large">
          <Typography>
            Your {itemText} been added to your list:&nbsp;
            <Link title={listName} href={`/list/view/details/${listId}`} weight="bold">
              {listName}
            </Link>
          </Typography>
        </Alert>

        {/* Pros Buy These Together - Only show for B2B customers on PDP/PIP confirmation */}
        {shouldShowCarousel && (
          <QueryProvider cacheKey="profbt-carousel" defaultVariables={defaultVariables}>
            <Suspense fallback={<div>Loading recommendations...</div>}>
              <BoughtTogetherCarousel
                itemId={firstProductId}
                listId={listId}
                membershipInformation={loyaltyMembership}
                onItemsAdded={setItemsAdded}
              />
            </Suspense>
          </QueryProvider>
        )}
      </DrawerBody>
      <DrawerFooter>
        <Button
          fullWidth
          variant="secondary"
          onClick={() => { onClose('ok'); }}
        >
          Close
        </Button>
      </DrawerFooter>
    </>
  );
};

AddToListSuccess.propTypes = {
  newListDetails: shape({
    listId: string,
    listName: string
  }).isRequired,
  onClose: func.isRequired,
  products: arrayOf(shape({
    itemId: string,
    quantity: string
  }))
};

AddToListSuccess.defaultProps = {
  products: []
};

AddToListSuccess.displayName = 'AddToListSuccess';

export { AddToListSuccess };
